/* global OverconstrainedError */
import { notify } from 'app/services/errorReporter';
import dumpMediaStream, { MediaStreamDump } from 'app/services/rtc/DeviceManager/dumpMediaStream';
import MediaDevices from 'app/services/rtc/MediaDevices';
import TrackState, { getStreamTrackState } from 'app/services/rtc/TrackState';
import WebAudio, { ReadonlyAnalyserNode } from 'app/services/WebAudio';
import { WithoutMethods } from 'app/utils/types';
import userAgent, { Browser, Platform } from 'app/utils/userAgent';
import { List } from 'immutable';
import uniqBy from 'lodash/uniqBy';
import { getStreamSettings } from 'app/models/rtc/selectors';

export const AUTOMATIC_DEVICE_ID = 'RtcAutomaticDevice';

export enum DeviceType {
  Audio = 'audio',
  Video = 'video',
}

export class DeviceInfo {
  public readonly id: string;
  public readonly label: string;
  public readonly error?: Error;

  public constructor({ id, label, error }: { id: string, label: string, error?: Error }) {
    this.id = id;
    this.label = label;
    this.error = error;
  }
}

export enum UserAction {
  None = '', // empty string for falsy value
  ConnectDevice = 'connectDevice',
  AskPermission = 'askPermission',
  MakeDeviceReadable = 'makeDeviceReadable', // when no readable devices are found
  InspectDeviceSettings = 'inspectDeviceSettings',
  ChangeBrowserSettings = 'changeBrowserSettings',
  ChangeBrowserSettingsAndRetry = 'changeBrowserSettingsAndRetry',
  ChangeBrowserSettingsAndReload = 'changeBrowserSettingsAndReload',
}

export enum AutoAction {
  None = '',
  Poll = 'poll',
  Watch = 'watch',
}

type DeviceSelector = Pick<MediaTrackConstraints, 'deviceId' | 'facingMode'>;

interface IUpdate {
  readonly stream?: MediaStream;
  readonly analyser?: ReadonlyAnalyserNode;
  readonly error?: Error;
  readonly trackState?: TrackState;
  readonly currentSettings?: MediaTrackSettings[];
  readonly preferredDeviceId?: string;
  readonly currentDeviceId?: string;
  readonly availableDevices?: List<DeviceInfo>;
  readonly behaviorOptions?: IBehaviorOptions;
  readonly autoGain?: boolean;
}

interface IDeviceStateProperties extends IUpdate {
  readonly type: DeviceType;
  readonly webAudio?: WebAudio;
  readonly constantConstraints: MediaTrackConstraints;
  readonly defaultSelector: DeviceSelector;
  readonly mirrorPreview?: boolean;
}

export interface IBehaviorOptions {
  /**
   * Determines if the device setup be manual. If not set to true
   * before starting the setup process, `DeviceState` will try to
   * automatically acquire access (provided the `isAccessAllowed()`
   * method returns `true`).
   *
   * If `manual` is `false` but `isAccessAllowed()` is set to false,
   * `manual` will be set to true so that the setup will continue in
   * manual mode and user is notified why they see browser prompts.
   *
   * It's set to `false` whenever the manual setup has been completed
   * once. When transitioning from `InspectState` to `ActiveState`.
   */
  readonly manual: boolean;
  /**
   * Does the user need to activate this device?
   */
  readonly required: boolean;
}

export interface IDeviceStateDebugDump
extends Omit<WithoutMethods<DeviceState>,
  'stream' | 'defaultSelector' | 'constantConstraints' | 'behaviorOptions' | 'webAudio'> {
  readonly stream?: MediaStreamDump;
}

export abstract class DeviceState implements IDeviceStateProperties {
  public readonly webAudio?: WebAudio;
  public readonly type: DeviceType;
  public readonly autoGain: boolean;
  public readonly trackState: TrackState;
  public readonly currentSettings: MediaTrackSettings[];
  public readonly constantConstraints: MediaTrackConstraints;
  public readonly defaultSelector: DeviceSelector;
  public readonly behaviorOptions: IBehaviorOptions;
  public readonly availableDevices: List<DeviceInfo>;
  public readonly preferredDeviceId: string;
  public readonly currentDeviceId?: string;
  public readonly stream?: MediaStream;
  public readonly analyser?: ReadonlyAnalyserNode;
  public readonly error?: Error;
  public readonly mirrorPreview: boolean;

  public constructor(prev: IDeviceStateProperties, update?: IUpdate) {
    this.type                = prev.type;
    this.autoGain            = prev.autoGain || false;
    this.webAudio            = prev.webAudio;
    this.trackState          = prev.trackState || TrackState.Off;
    this.currentSettings     = prev.currentSettings || [];
    this.constantConstraints = prev.constantConstraints || {};
    this.defaultSelector     = prev.defaultSelector;
    this.behaviorOptions     = prev.behaviorOptions || { manual: false, required: false };
    this.availableDevices    = prev.availableDevices || List();
    this.preferredDeviceId   = prev.preferredDeviceId || AUTOMATIC_DEVICE_ID;
    this.currentDeviceId     = prev.currentDeviceId;
    this.stream              = prev.stream;
    this.analyser            = prev.analyser;
    this.error               = prev.error;
    this.mirrorPreview       = prev.mirrorPreview || false;

    if (update) {
      Object.assign(this, update);
    }
  }

  public abstract step(): Promise<DeviceState>;

  public async setEnabled(enabled: boolean): Promise<DeviceState> { return this; }
  public async toggleEnabled(): Promise<DeviceState> {
    return this.setEnabled(this.trackState === TrackState.Muted ?
      false :
      this.trackState !== TrackState.Active,
    );
  }

  public get stage() { return this.constructor.name; }
  public get userAction() { return UserAction.None; }
  public get autoAction() { return AutoAction.None; }
  public get isInitial() { return false; }
  public get isSkipped() { return false; }
  public get isStreaming() { return this.stream !== undefined && this.userAction === UserAction.None; }
  public get needsReload() { return false; }

  /**
   * Get `constantConstraints` but filtered by what's supported on
   * current platform
   */
  public get defaultConstraints(): MediaTrackConstraints {
    return MediaDevices.filterTrackConstraints(this.constantConstraints);
  }

  /**
   * Get track constraints that reflect user's preferences. Uses
   * `exact` matching because otherwise `applyConstraints` tends to
   * not change the setting if it was `ideal: false` before. This
   * can cause `OverconstrainedError`.
   */
  public get preferenceConstraints(): MediaTrackConstraints {
    return MediaDevices.filterTrackConstraints({
      autoGainControl: { exact: this.autoGain },
    });
  }

  public *keys(): IterableIterator<keyof DeviceState> {
    yield 'stage';
    yield 'type';
    yield 'autoGain';
    yield 'defaultConstraints';
    yield 'preferenceConstraints';
    yield 'currentSettings';
    yield 'defaultSelector';
    yield 'availableDevices';
    yield 'preferredDeviceId';
    yield 'currentDeviceId';
    yield 'stream';
    yield 'analyser';
    yield 'trackState';
    yield 'error';
    yield 'userAction';
    yield 'autoAction';
    yield 'mirrorPreview';
  }

  public toJSON(): IDeviceStateDebugDump {
    const {
      type, stage, isInitial, isSkipped, isStreaming, needsReload, trackState, availableDevices,
      preferredDeviceId, currentDeviceId, error, userAction, autoAction, stream,
      autoGain, defaultConstraints, preferenceConstraints, currentSettings,
      mirrorPreview,
    } = this;

    return {
      type,
      stage,
      isInitial,
      isSkipped,
      isStreaming,
      needsReload,
      trackState,
      availableDevices,
      preferredDeviceId,
      currentDeviceId,
      autoGain,
      defaultConstraints,
      preferenceConstraints,
      currentSettings,
      error,
      userAction,
      autoAction,
      stream: stream && dumpMediaStream(stream),
      mirrorPreview,
    };
  }

  public setBehaviorOptions(behaviorOptions: IBehaviorOptions): DeviceState {
    // If we're in skipped state but the behaviour is set to manual,
    // move on to the initial state. This is to facilitate test
    // call being open in the same tab after a call where one of
    // the devices was muted.
    const Next = behaviorOptions.manual && this.isSkipped
      ? InitialDeviceState
      : this.constructor as any;

    return new Next(this, { behaviorOptions });
  }

  public skip(): DeviceState {
    return new SkippedDeviceState(this.stop());
  }

  public stop(): DeviceState {
    // Skip this device next time if it was already skipped or
    // muted explictly by user in the app
    const skip = this.isSkipped || this.trackState === TrackState.Disabled;

    this.stopTracks();

    const update = {
      stream: undefined,
      trackState: TrackState.Off,
      error: undefined,
      analyser: undefined,
    };

    if (skip) {
      return new SkippedDeviceState(this, update);
    } else {
      return new InitialDeviceState(this, update);
    }
  }

  public async restart() {
    return await this.stop().step();
  }

  public async updateAvailableDevices(): Promise<DeviceState> {
    return new (this.constructor as any)(this, { availableDevices: await this.getDevices() });
  }

  public async updateTrackState(): Promise<DeviceState> {
    return new (this.constructor as any)(this, {
      trackState: getStreamTrackState(this.stream),
      currentSettings: getStreamSettings(this.stream),
    });
  }

  public async setPreferredDeviceId(preferredDeviceId: string): Promise<DeviceState> {
    return new (this.constructor as any)(this, { preferredDeviceId });
  }

  public async setAutoGain(autoGain: boolean): Promise<DeviceState> {
    return new (this.constructor as any)(this, { autoGain });
  }

  public doDevicesExist(): boolean {
    return this.availableDevices.size > 0;
  }

  /**
   * Determines if access to the `this.type` class of devices is
   * currently allowed. It does it by checking if devices for this
   * type have labels, which seems to be universally-used side
   * effect of approving access across all browsers.
   *
   * Caveats:
   * - Safari 11 and lower have a bug that will expose labels as
   *   soon as any `RTCPeerConnection` is created. We're returning
   *   `false` just to be safe.
   * - Safari 12 for a while followed a little bit closer to what
   *   other browsers do but 12.1 started to emit `devicechange`
   *   events when the labels became available, for example when
   *   microphone was approved, camera would get the event and
   *   update labels, then this method would return a false positive.
   * - Firefox and Safari show labels for all devices as soon as one
   *   of them is approved, leading to possible false positives here.
   */
  public isAccessAllowed(): boolean {
    const ua = userAgent();

    if (ua.browser === Browser.Safari) {
      return false;
    }

    return this.availableDevices
      .filter(({ id }) => id !== AUTOMATIC_DEVICE_ID)
      .every(({ label }) => label.length > 0);
  }

  protected async getDevices(): Promise<List<DeviceInfo>> {
    const devices = uniqBy(// Firefox notably spits out duplicates on Linux
      (await MediaDevices.enumerateDevices())
        .filter(({ kind }) => kind === `${this.type}input`),
      ({ deviceId }) => deviceId,
    );

    if (devices.length === 0) {
      return List();
    } else {
      const automaticDevice = new DeviceInfo({ id: AUTOMATIC_DEVICE_ID, label: AUTOMATIC_DEVICE_ID });
      const deviceInfos = devices.map(({ deviceId, label }) => new DeviceInfo({ id: deviceId, label }));

      return List([automaticDevice, ...deviceInfos]);
    }
  }

  protected async requestAccess(): Promise<IUpdate> {
    let stream;
    let analyser;
    let currentDeviceId = AUTOMATIC_DEVICE_ID;
    let error;

    const { preferredDeviceId } = this;
    // Order devices in precedence: first the preferred deivice then the automatic device, then the rest
    const orderedDevices = (await this.getDevices()).sortBy(({ id }) => (
      id === preferredDeviceId ? 1 :
      id === AUTOMATIC_DEVICE_ID ? 2 :
      3
    ));

    // Try devices in order and fallback to the next one if the current is not readable
    for (const { id } of orderedDevices) {
      const selector = id === AUTOMATIC_DEVICE_ID ? this.defaultSelector : { deviceId: { exact: id } };
      try {
        error = undefined;
        stream = await this.selectDevice(selector);
        currentDeviceId = id;
        break;
      } catch (e) {
        error = e;
        if (isDeviceNotFoundError(e) || isHardwareOrOsError(e)) {
          continue; // try another device
        } else {
          break;
        }
      }
    }

    const trackState = getStreamTrackState(stream);

    // After an update we want to create an audio analyser if possible.
    if (this.webAudio && stream && stream.getAudioTracks().length > 0 && trackState !== TrackState.Off) {
      try {
        analyser = this.webAudio.makeAnalyser(stream, {
          fftSize: 256,
          smoothingTimeConstant: 0.5,
        });
      } catch (err) {
        // Failed to create an analyser -> make a bogus one and notify honeybadger.
        // Using a bogus one saves us from completely killing audio if we failed
        // to create an analyser.
        analyser = this.webAudio.makeDummyAnalyser();
        notify(err);
      }
    }

    const currentSettings = getStreamSettings(stream);
    return { analyser, stream, error, currentDeviceId, trackState, currentSettings };
  }

  protected stopTracks() {
    if (this.stream) this.stream.getTracks().forEach((track) => track.stop());
  }

  private async selectDevice(selector: DeviceSelector) {
    try {
      return await MediaDevices.getUserMedia({
        [this.type]: {
          ...this.defaultConstraints,
          ...this.preferenceConstraints,
          ...selector,
        },
      });
    } catch (error) {
      // Try to request without user preferences (which use `exact`
      // constraints so it's possible they might fail)
      if (isOverconstrainedError(error)) {
        return await MediaDevices.getUserMedia({
          [this.type]: {
            ...this.defaultConstraints,
            ...selector,
          },
        });
      } else {
        throw error;
      }
    }
  }
}

const isDeviceNotFoundError = (error: { name?: string, constraint?: string, constraintName?: string }) =>
  error.name === 'NotFoundError' ||        // spec name
  error.name === 'DevicesNotFoundError' || // Chrome name
  error.constraint === 'deviceId' ||       // spec prop
  error.constraintName === 'deviceId';     // Chrome prop

const isHardwareOrOsError = ({ name }: { name: string }) =>
  name === 'NotReadableError' || // spec
  name === 'AbortError' ||       // spec
  name === 'InternalError' ||    // FF
  name === 'TrackStartError';   // Chrome

const isOverconstrainedError = (error: Error) => (
  // OverconstrainedError.name is just 'Error' in Safari, so we need this condition
  (typeof OverconstrainedError !== 'undefined' && error instanceof OverconstrainedError) ||
  // OverconstrainedError does not exist in Firefox, so we need this condition
  error.name === 'OverconstrainedError'
);

export class InitialDeviceState extends DeviceState {
  public get isInitial() { return true; }

  public async step(): Promise<DeviceState> {
    if (this.doDevicesExist()) {
      return await (new DevicesAvailableState(this).step());
    } else {
      return new NoDevicesFoundState(this);
    }
  }
}

class SkippedDeviceState extends DeviceState {
  public get isSkipped() { return true; }

  public async step() {
    return new InitialDeviceState(this).step();
  }

  // Unmuting the device should also trigger activation
  public async setEnabled(enabled: boolean) {
    return enabled ? this.step() : this;
  }
}

class NoDevicesFoundState extends DeviceState {
  public get userAction() { return UserAction.ConnectDevice; }
  public get autoAction() { return AutoAction.Watch; }

  public async step(): Promise<DeviceState> {
    // Update devices snapshot is necessary here to know if we have new
    // devices
    const next = await this.updateAvailableDevices();

    if (next.doDevicesExist()) {
      return await (new DevicesAvailableState(next).step());
    } else {
      return next;
    }
  }
}

// Intermediate state that checks if access is pre-allowed, decides
// whether attempt to request device automatically or not
class DevicesAvailableState extends DeviceState {
  // In case access was pra-approved, go to the active state, unless overriden by caller
  public async step(): Promise<DeviceState> {
    const next = new RequestDeviceState(this);

    if (!this.behaviorOptions.manual && this.isAccessAllowed()) {
      return await next.step();
    } else {
      // If access was not pre-approved, show warning about asking for
      // user's permission
      return next.setBehaviorOptions({ ...this.behaviorOptions, manual: true });
    }
  }
}

class RequestDeviceState extends DeviceState {
  public get userAction() { return UserAction.AskPermission; }

  // When called by user's action, default to InspectState
  public async step(): Promise<DeviceState> {
    const response = await this.requestAccess();

    if (response.stream) {
      const { manual } = this.behaviorOptions;
      const SuccessState = manual ? InspectState : ActiveState;
      const next = new SuccessState(this, response);

      // After we got a stream back, labels for devices should be available now
      return await next.updateAvailableDevices();
    } else if (response.error && isHardwareOrOsError(response.error)) {
      return new MakeDeviceReadableState(this, response);
    } else {
      const { platform, browser } = userAgent();
      if (browser === Browser.Firefox) {
        return new ChangeBrowserSettingsAndRetryState(this, response);
      } else if (platform === Platform.PC && browser === Browser.Chrome) {
        return new PollingState(this, response);
      } else {
        return new ChangeBrowserSettingsAndReloadState(this, response);
      }
    }
  }
}

class ActiveState extends DeviceState {
  public async step(): Promise<DeviceState> {
    return this; // final state, do nothing
  }

  public async setEnabled(enabled: boolean): Promise<DeviceState> {
    if (!this.stream) { return this; }

    for (const track of this.stream.getTracks()) {
      track.enabled = enabled;
    }

    return await this.updateTrackState();
  }

  public async setPreferredDeviceId(preferredDeviceId: string) {
    return await this.rerequest({ preferredDeviceId });
  }

  public async setAutoGain(autoGain: boolean) {
    try {
      const next = await super.setAutoGain(autoGain);

      if (!next.stream) {
        return next;
      }

      for (const track of next.stream.getTracks()) {
        await track.applyConstraints({
          ...next.defaultConstraints,
          ...next.preferenceConstraints,
        });
      }

      return next.updateTrackState();
    } catch (error) {
      // OverconstrainedError will particularly always happen in
      // Chrome because it doesn't allow to change autogain settings
      // without a getting a fresh stream.
      if (isOverconstrainedError(error)) {
        return this.rerequest({ autoGain });
      } else {
        throw error;
      }
    }
  }

  public async updateTrackState(): Promise<DeviceState> {
    const trackState = getStreamTrackState(this.stream);
    const currentSettings = getStreamSettings(this.stream);

    if (trackState === TrackState.Off) {
      // In case the track stopped, go back to initial state and try
      // resolve the issue, depending on the situation user will see a
      // prompt (if not pre-approved) or another device will be selected.
      let next = new InitialDeviceState(this, { trackState, currentSettings });

      // Update devices in case the track was stopped because a device was unplugged
      next = await next.updateAvailableDevices();

      return await next.step();
    } else {
      return super.updateTrackState();
    }
  }

  private async rerequest(update: IUpdate) {
    this.stopTracks();
    const next = new RequestDeviceState(this, update);
    return await next.step();
  }
}

class InspectState extends ActiveState {
  public get userAction() { return UserAction.InspectDeviceSettings; }

  public async step() {
    return new ActiveState(this).setBehaviorOptions({ ...this.behaviorOptions, manual: false });
  }
}

class ChangeBrowserSettingsAndReloadState extends DeviceState {
  public get userAction() { return UserAction.ChangeBrowserSettingsAndReload; }
  public get needsReload() { return true; }

  public async step(): Promise<DeviceState> {
    // This is a dead end, user needs to reload the browser after changing settings
    return this;
  }
}

// Change settings and try again manually, only Firefox on all platforms
class ChangeBrowserSettingsAndRetryState extends DeviceState {
  public get userAction() { return UserAction.ChangeBrowserSettingsAndRetry; }

  public async step(): Promise<DeviceState> {
    if (!this.doDevicesExist()) {
      return new NoDevicesFoundState(this);
    }

    const next = new RequestDeviceState(this);
    return await next.step();
  }
}

// Change settings and poll for updated settings by checking labels in the list of devices, only Chrome
class PollingState extends DeviceState {
  public get userAction() { return UserAction.ChangeBrowserSettings; }
  public get autoAction() { return AutoAction.Poll; }

  public async step(): Promise<DeviceState> {
    // Update for the sake of checking if labels are there
    const next = await this.updateAvailableDevices();

    if (!next.doDevicesExist()) {
      return new NoDevicesFoundState(next);
    }

    if (next.isAccessAllowed()) {
      return await (new RequestDeviceState(next)).step();
    } else {
      return next;
    }
  }
}

class MakeDeviceReadableState extends PollingState {
  public get userAction() { return UserAction.MakeDeviceReadable; }
}
